import React, { PropsWithChildren, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useFocusOnRefreshWithTabIndex, useRefreshBlock } from '../../BlockFetcher';
import { Heading } from '../Heading/Heading';
import { PostsInteractions } from './partials/PostInteractions';

import { PostDeleteForm } from './forms/PostDeleteForm';

import { PostBody } from './partials/PostBody';
import { PostFirst, PostReply, PostTypes, isPostFirst } from './Post.types';
import IForm from '../../../types/IForm';

export type PostProps = {
  post: PostFirst | PostReply;
  headingHierarchy: number;
  previousPostId: string | undefined;
  type: PostTypes;
  replyEditorId?: string;
  currentForm: IForm | undefined;
  setCurrentForm: (newState: IForm | undefined) => void;
  postNumber: number | string; // can be 4 or '4.2',
};

export function Post({
  headingHierarchy,
  previousPostId,
  children,
  post,
  currentForm,
  setCurrentForm,
  replyEditorId,
  type,
  postNumber,
}: PropsWithChildren<PostProps>) {
  const { t } = useSonTranslation('Posts');
  const { hash } = useLocation();
  const { refresh, refreshCount, refreshProps } = useRefreshBlock();
  const focusRef = useFocusOnRefreshWithTabIndex<HTMLDivElement>(refreshCount);

  const closeDeleteModal = () => setCurrentForm(undefined);

  const { id } = post;

  /**
   * Ids for editor containers toggled via skip links (edit and reply "buttons")
   * */
  const editEditorId = `edit-editor-${id}`;

  /**
   * When update editor of a reply is replaced by reply editor from post.
   * We set currentForm to undefined to make sure, edit button is shown
   * */
  useEffect(() => {
    if (!hash.includes(id) && currentForm === post?.updateForm) {
      setCurrentForm(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  return (
    <>
      <div className="posts__post" id={id} data-type={type}>
        <Heading
          id={`post-heading-${id}`}
          className="invisible"
          hierarchy={headingHierarchy}
          ref={id === refreshProps ? focusRef : null}
        >
          {t(`default.heading.post.${type}`, { number: postNumber })}
        </Heading>
        <div className="posts__body-interactions-container">
          <PostBody
            post={post}
            headingHierarchy={headingHierarchy}
            type={type}
            editEditorId={editEditorId}
            currentForm={currentForm}
            hash={hash}
          />
          <PostsInteractions
            postId={id}
            replyForm={isPostFirst(post) ? post.replyForm : undefined}
            updateForm={post?.updateForm}
            deleteForm={post?.deleteForm}
            setCurrentForm={setCurrentForm}
            currentForm={currentForm}
            type={type}
            editEditorId={editEditorId}
            replyEditorId={replyEditorId}
          />
        </div>
        {children}
      </div>

      {currentForm && currentForm === post.deleteForm && (
        <PostDeleteForm
          id={id}
          closeModal={closeDeleteModal}
          type={type}
          currentForm={currentForm}
          refresh={() => {
            refresh(previousPostId);
          }}
        />
      )}
    </>
  );
}
